import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ProjectTable } from "./project-table/ProjectTable.tsx";
import JobAllotmentWizard from "@/components/JobAllotmentWizard";
import ClaudeOperationsPanel from "@/components/ClaudeOperationsPanel";
import {
  BellRing,
  CheckCircle2,
  Factory,
  FileCheck2,
  PackageSearch,
  PencilRuler,
} from "lucide-react";

const ROLE_LABELS = {
  po_incharge: "PO Incharge",
  drawings_incharge: "DRG Incharge",
  design_incharge: "Design Incharge",
  production_incharge: "Prod Incharge",
};

function detailValue(order, title) {
  return (
    (order.details || []).find((item) => item.title === title)?.value || ""
  );
}

function activeStage(order) {
  return (order.stages || []).find((stage) => stage.status === "active") || {};
}

function numericDetail(value) {
  return Number(String(value || "").replace(/[^0-9.-]/g, "")) || 0;
}

function receiptItems(po) {
  const received = {};
  (po.grns || []).forEach((grn) => {
    (grn.entries || []).forEach((entry) => {
      received[entry.item_id] = (received[entry.item_id] || 0) + Number(entry.qty_received || 0);
    });
  });

  return (po.items || []).map((item) => {
    const orderedQty = Number(item.qty || 0);
    const receivedQty = Math.min(received[item.id] || 0, orderedQty);
    const pendingQty = Math.max(0, orderedQty - receivedQty);
    return {
      id: item.id,
      description: item.item || "Unnamed item",
      orderedQty,
      receivedQty,
      pendingQty,
      unit: item.unit || "Nos",
      itemCode: item.cat_no,
      expectedDate: po.required_date,
      status: pendingQty === 0 ? "Received" : receivedQty > 0 ? "Partially Received" : "Pending",
    };
  });
}

function fileBlob(dataUrl) {
  const [header, encoded] = String(dataUrl || "").split(",");
  if (!header || !encoded) {
    throw new Error("The uploaded file data is unavailable.");
  }
  const contentType =
    (header.match(/data:(.*?);/) || [])[1] || "application/octet-stream";
  const binary = window.atob(encoded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: contentType });
}

function projectDocuments(order) {
  const folderNames = Object.fromEntries(
    (order.document_folders || []).map((folder) => [folder.id, folder.name]),
  );

  return (order.attachments || [])
    .filter((attachment) => !attachment.superseded)
    .map((attachment) => ({
      id: attachment.id,
      name: attachment.name || "Unnamed file",
      size: Number(attachment.size) || 0,
      type: attachment.type || attachment.content_type || "application/octet-stream",
      category: attachment.category || "Other Document",
      folder:
        attachment.folder_name ||
        folderNames[attachment.folder_id] ||
        attachment.category ||
        "General",
      uploadedAt: attachment.uploaded_at,
      uploadedBy: attachment.uploaded_by,
      status:
        attachment.approval_status === "approved"
          ? "Approved"
          : attachment.approval_status === "rejected"
            ? "Rejected"
            : "Pending review",
    }));
}

function displayDveplCode(value) {
  return String(value || "—").replace(/^DVEPL[-\s]*/i, "");
}

function toProject(order, purchaseOrders) {
  const active = activeStage(order);
  const customer = order.customer_master || {};
  const contactPerson =
    order.contact_person ||
    order.contact_name ||
    customer.contact_person ||
    "";
  const mobileNumber =
    order.customer_whatsapp ||
    order.customer_mobile ||
    order.mobile ||
    customer.mobile ||
    customer.whatsapp ||
    "";
  const customerEmail = order.customer_email || customer.email || "";
  const roleLabels = Object.fromEntries(
    (order.role_definitions || []).map((role) => [role.key, role.label]),
  );
  const roles = Object.entries(order.role_assignments_names || {}).map(
    ([key, person]) => ({
      role: roleLabels[key] || ROLE_LABELS[key] || key,
      person: Array.isArray(person) ? person.join(", ") : person || "Unassigned",
      department: "Operations",
      badgeColor: "emerald",
    }),
  );
  const items = (order.po_items || []).map((item, index) => ({
    id: `${order.id}-item-${index}`,
    itemNumber: item.legacy_item_no || index + 1,
    name: item.description || "Imported item",
    qty: Number(item.qty) || 0,
    unit: item.unit || "Nos",
    unitPrice: Number(item.rate) || 0,
    totalPrice: (Number(item.qty) || 0) * (Number(item.rate) || 0),
  }));
  const vendorPOs = purchaseOrders.map((po) => ({
    id: po.id,
    poNumber: po.po_no,
    poDate: po.po_date,
    vendorName: po.vendor_name,
    concernedPerson: po.concerned_person,
    mobileNo: po.contact_phone,
    emailId: po.contact_email,
    amountWithoutGst: po.subtotal,
    gstAmount: po.gst_amount,
    amountWithGst: po.grand_total,
    materialStatus: po.status,
    status: po.status,
    expectedDate: po.required_date,
    items: receiptItems(po),
  }));
  const lineItemTotal = items.reduce((total, item) => total + item.totalPrice, 0);
  const importedBasic = numericDetail(detailValue(order, "Imported Basic Value"));
  const wizardBasic = numericDetail(detailValue(order, "Basic Value"));
  const basic = lineItemTotal || importedBasic || wizardBasic;
  const gst = Math.round(basic * 0.18 * 100) / 100;
  const target =
    order.legacy_target_text || detailValue(order, "Target Month / Commitment");

  return {
    id: order.id,
    sNo: order.order_no || order.customer_code,
    dveplCode: displayDveplCode(order.customer_code || order.order_no),
    orderConfirmDate: order.order_date,
    orderTakenBy: order.taken_by_name || "—",
    deliveryTarget: target || order.target_dispatch || "Pending",
    concernedPerson: roles.map((role) => role.person).join(", "),
    concernedPersons: roles.map((role) => role.person),
    projectRoleAssignments: roles,
    partyName: order.customer || order.name,
    companyName: order.customer || order.name,
    contactPerson,
    contactPhone: mobileNumber,
    mobileNo: mobileNumber,
    partyEmail: customerEmail,
    emailId: customerEmail,
    caNumber:
      order.customer_po ||
      detailValue(order, "Project Reference / CA No") ||
      detailValue(order, "Project Reference"),
    itemDescription:
      detailValue(order, "Project Reference / CA No") ||
      detailValue(order, "Project Reference") ||
      "Imported OIHR order",
    items,
    amount: basic,
    gst,
    totalAmount: basic + gst,
    advance: numericDetail(detailValue(order, "Legacy Advance")),
    balance: numericDetail(detailValue(order, "Legacy Balance")),
    stage: active.name || "Workflow active",
    priority: String(order.priority || "normal").replace(/^./, (char) =>
      char.toUpperCase(),
    ),
    materialStatus: detailValue(order, "Legacy Remark"),
    vendorName:
      vendorPOs[0]?.vendorName || detailValue(order, "Legacy PO Vendor"),
    poNumber: vendorPOs[0]?.poNumber || "",
    hasCreatedPO: vendorPOs.length > 0,
    vendorPOs,
    drawingApprovedDate: detailValue(order, "Drawing Approved Date"),
    drawingSendDate: order.order_date,
    remark: detailValue(order, "Legacy Remark"),
    followUpCount: 0,
    autoFollowUpEnabled: Boolean(order.reminders_enabled),
    followUpCadenceDays: order.reminder_value || 2,
    milestones: (order.stages || []).map((stage) => ({
      id: stage.key,
      title: stage.name,
      completed: stage.status === "completed",
      completedAt: stage.completed_at,
      assignedTo: stage.assignee_name,
    })),
    createdAt: order.created_at,
    updatedAt: order.updated_at || order.created_at,
    documents: projectDocuments(order),
    _liveOrder: order,
  };
}

const KPI_DEFINITIONS = [
  { key: "total", label: "Total Projects", icon: PackageSearch },
  { key: "drawing", label: "Drawing Approval", icon: PencilRuler },
  { key: "po", label: "Component / PO Pending", icon: FileCheck2 },
  { key: "production", label: "Shopfloor In-Process", icon: Factory },
  { key: "reminders", label: "Reminders Due", icon: BellRing },
  { key: "active", label: "Active Jobs", icon: CheckCircle2 },
];

function MissionControlKpis({ orders, purchaseOrders, className = "" }) {
  const values = useMemo(() => {
    const activeOrders = orders.filter((order) => order.status !== "completed");
    const stageLabel = (order) => {
      const stage = activeStage(order);
      return `${stage.name || ""} ${stage.key || ""}`.toLowerCase();
    };
    const dueSoon = activeOrders.filter((order) => {
      if (!order.target_dispatch) return false;
      const days = (new Date(order.target_dispatch) - Date.now()) / 86400000;
      return Number.isFinite(days) && days >= 0 && days <= 7;
    });

    return {
      total: orders.length,
      totalCompanies: new Set(
        orders.map((order) => order.customer || order.name).filter(Boolean),
      ).size,
      drawing: activeOrders.filter((order) =>
        /drawing|design|approval/.test(stageLabel(order)),
      ).length,
      po: purchaseOrders.filter((po) =>
        ["draft", "sent", "accepted"].includes(po.status),
      ).length,
      production: activeOrders.filter((order) =>
        /production|manufactur|fabrication|wiring/.test(stageLabel(order)),
      ).length,
      reminders: dueSoon.length,
      active: activeOrders.length,
    };
  }, [orders, purchaseOrders]);

  return (
    <section
      className={`grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6 ${className}`}
      data-testid="mission-control-kpis"
    >
      {KPI_DEFINITIONS.map(({ key, label, icon: Icon }) => (
        <div
          key={key}
          className="h-[82px] min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 p-2.5 shadow-sm"
          data-testid={`mission-kpi-${key}`}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="min-w-0 text-[9px] font-extrabold uppercase tracking-wider text-slate-500">
              {label}
            </span>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
              <Icon size={14} strokeWidth={2.3} />
            </span>
          </div>
          {key === "total" ? (
            <div
              className="mt-1 font-heading text-[12px] font-extrabold leading-[1.1] text-emerald-800"
              data-testid="mission-kpi-total-counts"
            >
              <span className="block tabular-nums">
                {values.totalCompanies} Companies
              </span>
              <span className="block tabular-nums">{values.total} Projects</span>
            </div>
          ) : (
            <div className="mt-1 font-heading text-2xl font-extrabold tabular-nums leading-tight text-emerald-800">
              {values[key]}
            </div>
          )}
          <div className="mt-0.5 text-[8px] font-medium text-slate-400">
            Live operational count
          </div>
        </div>
      ))}
    </section>
  );
}

export default function LiveProjectTable() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [featurePermissions, setFeaturePermissions] = useState(null);

  const load = () => {
    api.get("/orders").then((response) => setOrders(response.data));
    api
      .get("/purchase-orders")
      .then((response) => setPurchaseOrders(response.data));
    api
      .get("/users?active=true")
      .then((response) => setEngineers(response.data));
    api
      .get("/feature-permissions/me")
      .then((response) => setFeaturePermissions(response.data.permissions));
  };

  useEffect(() => {
    load();
  }, []);

  const projects = useMemo(() => {
    return orders.map((order) => {
      const linked = purchaseOrders.filter((po) => po.order_id === order.id);
      return toProject(order, linked);
    });
  }, [orders, purchaseOrders]);

  const canCreateJob = Boolean(featurePermissions?.create_job);
  const canEditDeleteJob = Boolean(featurePermissions?.edit_delete_job);

  const deleteOrders = async (selectedProjects) => {
    if (!canEditDeleteJob || !selectedProjects.length) return;
    await Promise.all(
      selectedProjects.map((project) => api.delete(`/orders/${project.id}`)),
    );
    load();
  };

  const exportCsv = async () => {
    const response = await api.get("/orders/export", { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = "jobs-tracking-sheet.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const readDocumentBlob = async (project, attachment) => {
    const response = await api.get(
      `/orders/${project.id}/attachments/${attachment.id}/data`,
    );
    const dataUrl = response?.data?.data;

    if (!dataUrl) {
      throw new Error("The uploaded file data is unavailable.");
    }

    return fileBlob(dataUrl);
  };

  const openDocument = async (project, attachment) => {
    const preview = window.open("about:blank", "_blank");

    try {
      const blob = await readDocumentBlob(project, attachment);
      const url = URL.createObjectURL(blob);

      if (preview && !preview.closed) {
        preview.location.href = url;
      } else {
        window.open(url, "_blank");
      }

      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (preview && !preview.closed) {
        preview.close();
      }

      const message =
        error?.response?.data?.detail ||
        error?.message ||
        "This file could not be opened with your current access.";
      toast.error(message);
    }
  };

  const downloadDocument = async (project, attachment) => {
    try {
      const blob = await readDocumentBlob(project, attachment);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");

      link.href = url;
      link.download = attachment.name || "download";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        "This file could not be downloaded with your current access.";
      toast.error(message);
    }
  };

  return (
    <div
      className="min-h-full bg-[#f6f8f5] px-3 py-4 lg:px-5"
      data-testid="mission-control-page"
    >
      <ProjectTable
        projects={projects}
        engineers={engineers.map((engineer) => ({
          name: engineer.name,
          role: engineer.role,
        }))}
        isAdmin={canEditDeleteJob}
        onNewJob={canCreateJob ? () => setWizardOpen(true) : undefined}
        onOpenDetailSheet={(project) => navigate(`/orders/${project.id}`)}
        onOpenEditModal={
          canEditDeleteJob
            ? (project) => navigate(`/orders/${project.id}?edit=1`)
            : undefined
        }
        onOpenEmailModal={(project) => navigate(`/orders/${project.id}`)}
        onManagePOs={(project) => navigate(`/orders/${project.id}`)}
        onOpenStoreDesk={(project) => navigate(`/orders/${project.id}`)}
        onUpdateStage={(project) => navigate(`/orders/${project.id}`)}
        onToggleMilestone={(project) => navigate(`/orders/${project.id}`)}
        onReassignEngineer={(project) => navigate(`/orders/${project.id}`)}
        onDeleteProject={
          canEditDeleteJob ? (project) => deleteOrders([project]) : undefined
        }
        onDeleteMultipleProjects={canEditDeleteJob ? deleteOrders : undefined}
        onExportCsv={exportCsv}
        onOpenDocument={openDocument}
        onDownloadDocument={downloadDocument}
        headerSummary={
          <MissionControlKpis orders={orders} purchaseOrders={purchaseOrders} />
        }
      />
      <ClaudeOperationsPanel orders={orders} />
      <JobAllotmentWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onCreated={(order) => {
          load();
          navigate(`/orders/${order.id}`);
        }}
      />
    </div>
  );
}
